import { ReactNode } from "react";
import { Box, Button, Chip, MenuItem, Skeleton, TextField, Typography } from "@mui/material";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import {
  MetricSignal,
  RevenueSnapshot as Snapshot,
  RevenueSnapshotMetric,
  RevenueSnapshotMetricUnit,
  RevenueSnapshotUnavailableReason,
  RevenueSnapshotValueStatus,
  RevOpsBrief,
} from "Models/RevOpsBrief";
import { advisor, InfoHint } from "Components/AdvisorTheme";
import { formatCreatedAt, isStaleBrief } from "Components/TodaysBrief";
import { PipelineSelection } from "./usePipelineSelection";

// ─── Formatting ───────────────────────────────────────────────────────────────

export const humanizeMetric = (metric: string) => {
  const words = metric.replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** Signals from every brief list; the first occurrence of each metric per pipeline wins. */
export const uniqueSignals = (brief: RevOpsBrief): MetricSignal[] =>
  [...(brief.metric_changes ?? []), ...(brief.risks ?? []), ...(brief.opportunities ?? [])]
    .filter((signal, i, all) => all.findIndex(other => other.metric === signal.metric && other.pipeline === signal.pipeline) === i);

export const formatComparison = (pct: number | null) =>
  pct == null ? "Unavailable" : `${pct > 0 ? "+" : ""}${pct.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;

const number = (value: number, digits: number) => value.toLocaleString(undefined, { maximumFractionDigits: digits });

export interface FormattedValue { text: string; suffix?: string; full?: string }

/**
 * Formats a value from its explicit unit. `ratio` is a 0..1 fraction, so it is
 * shown as a percent without dividing by 100. A currency symbol appears only
 * when a currency code is supplied.
 */
export function formatMetricValue(value: number | null, unit: RevenueSnapshotMetricUnit, currencyCode: string | null): FormattedValue {
  if (value == null) return { text: "Unavailable" };
  switch (unit) {
    case "ratio":
      return { text: value.toLocaleString(undefined, { style: "percent", maximumFractionDigits: 1 }) };
    case "days":
      return { text: number(value, 1), suffix: value === 1 ? " day" : " days" };
    case "currency_amount": {
      const compact = Math.abs(value) >= 100000;
      const options: Intl.NumberFormatOptions = currencyCode
        ? { style: "currency", currency: currencyCode, maximumFractionDigits: compact ? 1 : 0 }
        : { maximumFractionDigits: compact ? 1 : 0 };
      const full = value.toLocaleString(undefined, currencyCode ? { style: "currency", currency: currencyCode } : { maximumFractionDigits: 2 });
      return { text: value.toLocaleString(undefined, compact ? { ...options, notation: "compact" } : options), full };
    }
    default:
      return { text: number(value, 2) };
  }
}

const VALUE_STATUS_NOTE: Record<Exclude<RevenueSnapshotValueStatus, "available">, string> = {
  stored_null: "Not computable for this brief",
  missing: "Not recorded in this brief",
  invalid: "Stored value could not be read",
};

// Briefs from snapshot_version 3 describe the pipeline as it stands; older briefs
// keep their original definitions, so wording follows the brief's version.
const OPEN_PIPELINE_VERSION = 3;
const isOpenPipelineSnapshot = (snapshot?: Snapshot) => (snapshot?.snapshot_version ?? 0) >= OPEN_PIPELINE_VERSION;

// stored_null meanings from the backend contract's metric definitions.
const STORED_NULL_REASON: Record<string, string> = {
  win_rate: "No deals created in the last 30 days have closed yet",
  avg_days_in_stage: "No stage durations for deals created since yesterday",
};

const windowText = (metric: RevenueSnapshotMetric) =>
  metric.details?.window_days ? `the last ${metric.details.window_days} days` : "the recent window";

const STORED_NULL_REASON_V3: Record<string, (metric: RevenueSnapshotMetric) => string> = {
  win_rate: metric => `No deals in this pipeline closed in ${windowText(metric)}`,
  avg_days_in_stage: () => "No time recorded in this pipeline’s open stages yet",
};

const UNAVAILABLE_MESSAGE: Record<RevenueSnapshotUnavailableReason, string> = {
  no_metrics: "This brief has no stored pipeline metrics.",
  empty_metrics: "No pipeline metrics were captured for this brief.",
  legacy_unscoped_metrics: "This older brief stored tenant-wide metrics without a pipeline, so they are not shown.",
  invalid_metrics: "This brief’s stored metrics could not be read.",
};

// Definitions from the backend contract (rvo-backend docs/revops-brief-contract.md).
const DEFINITIONS_V3: Record<string, string> = {
  pipeline_value: "Open Pipeline Value: total amount of deals currently in an open stage (not closed won or lost). Currency is not specified by the source.",
  deal_count: "Open Deals: deals currently in an open stage.",
  win_rate: "Win Rate: deals won ÷ deals closed (won + lost) with a close date in the trailing window shown on the card; each deal counts once.",
  avg_days_in_stage: "Avg Days in Stage: how long deals typically spend in each open stage, averaged across every deal in this pipeline’s history.",
};

const DEFINITIONS: Record<string, string> = {
  pipeline_value: "Pipeline Value: sum of deal amounts across every current stage, including closed won and lost, so it is not open pipeline. Currency is not specified by the source.",
  deal_count: "Deal Count: deals across the same stages as Pipeline Value, including closed stages.",
  win_rate: "Win Rate: wins ÷ (wins + losses) among deals created in the last 30 days that have closed, weighted per entered stage. It is not a rep’s win/loss record.",
  avg_days_in_stage: "Avg Days in Stage: unweighted mean stage duration for deals created since the day before the brief. It is not time to close.",
};

// ─── Pieces ───────────────────────────────────────────────────────────────────

function Comparison({ pct, period }: { pct: number | null; period: "week" | "day" }) {
  if (pct == null) {
    return <Typography sx={{ fontSize: 13, color: advisor.subtle }}>No prior-{period} comparison</Typography>;
  }
  const Icon = pct > 0 ? ArrowUpwardRoundedIcon : pct < 0 ? ArrowDownwardRoundedIcon : RemoveRoundedIcon;
  const color = pct > 0 ? advisor.up : pct < 0 ? advisor.down : advisor.subtle;
  return (
    <Typography sx={{ fontSize: period === "week" ? 14 : 13, color: advisor.muted, display: "flex", alignItems: "center", columnGap: 0.5, flexWrap: "wrap" }}>
      <Box component="span" sx={{ color, fontWeight: 700, display: "inline-flex", alignItems: "center" }}>
        <Icon aria-hidden sx={{ fontSize: 15 }} />
        {Math.abs(pct).toLocaleString(undefined, { maximumFractionDigits: 1 })}%
      </Box>
      <span>vs. prior {period}</span>
    </Typography>
  );
}

const cardSx = {
  bgcolor: advisor.card,
  border: `1px solid ${advisor.border}`,
  borderRadius: advisor.radius,
  boxShadow: advisor.shadow,
  p: { xs: 1.75, sm: 2.25 },
  minWidth: 0,
};

/** Up to four columns on wide screens; extra metrics wrap onto further rows. */
const gridFor = (n: number) => ({
  display: "grid",
  gap: { xs: 1.5, md: 2 },
  gridTemplateColumns: {
    xs: n === 1 ? "1fr" : "repeat(2, minmax(0, 1fr))",
    md: `repeat(${Math.min(Math.max(n, 1), 4)}, minmax(0, 1fr))`,
  },
  // An odd final card spans the row on two-column layouts.
  ...(n > 1 && n % 2 === 1 ? { "& > :last-of-type": { gridColumn: { xs: "1 / -1", md: "auto" } } } : {}),
});

interface CardProps { label: string; value: FormattedValue; note?: string; children?: ReactNode }

function MetricCard({ label, value, note, children }: CardProps) {
  const unavailable = value.text === "Unavailable";
  return (
    <Box component="article" aria-label={label} sx={cardSx}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: advisor.muted, overflowWrap: "anywhere", lineHeight: 1.3 }}>{label}</Typography>
      <Typography
        title={value.full}
        sx={{ fontSize: unavailable ? { xs: 20, sm: 22 } : { xs: 26, sm: 30 }, fontWeight: 700, color: unavailable ? advisor.subtle : advisor.text, lineHeight: 1.2, my: 0.75, overflowWrap: "anywhere" }}
      >
        {value.text}
        {value.suffix && <Box component="span" sx={{ fontSize: 15, fontWeight: 600, color: advisor.muted }}>{value.suffix}</Box>}
      </Typography>
      {note && <Typography sx={{ fontSize: 12.5, color: advisor.subtle, mb: 0.25 }}>{note}</Typography>}
      {children}
    </Box>
  );
}

function storedNullReason(metric: RevenueSnapshotMetric, openPipeline: boolean): string {
  if (openPipeline) return STORED_NULL_REASON_V3[metric.key]?.(metric) ?? VALUE_STATUS_NOTE.stored_null;
  return STORED_NULL_REASON[metric.key] ?? VALUE_STATUS_NOTE.stored_null;
}

/** "10 won · 64 lost · last 180 days" for a v3 win rate with stored counts. */
export function winRateCounts(metric: RevenueSnapshotMetric): string | undefined {
  const d = metric.details;
  if (metric.key !== "win_rate" || !d || metric.value_status !== "available") return undefined;
  return [`${d.wins.toLocaleString()} won`, `${d.losses.toLocaleString()} lost`, d.window_days ? `last ${d.window_days} days` : null]
    .filter(Boolean).join(" · ");
}

function SnapshotMetricCard({ metric, currencyCode, openPipeline }: { metric: RevenueSnapshotMetric; currencyCode: string | null; openPipeline: boolean }) {
  const value = formatMetricValue(metric.value, metric.unit, currencyCode);
  const note = metric.value_status === "stored_null"
    ? storedNullReason(metric, openPipeline)
    : metric.value_status !== "available"
    ? VALUE_STATUS_NOTE[metric.value_status]
    : metric.unit === "currency_amount" && !currencyCode ? "Currency not specified"
    : openPipeline ? winRateCounts(metric) : undefined;
  return (
    <MetricCard label={metric.label} value={value} note={note}>
      {metric.value_status === "available" && (metric.comparison
        ? <><Comparison pct={metric.comparison.wow_pct} period="week" /><Comparison pct={metric.comparison.dod_pct} period="day" /></>
        : <Typography sx={{ fontSize: 13, color: advisor.subtle }}>No comparison available</Typography>)}
    </MetricCard>
  );
}

// Older backends only: signals carry no unit, so units come only from the metric name.
function SignalCard({ signal }: { signal: MetricSignal }) {
  const unit: RevenueSnapshotMetricUnit = /(^|[_\s])days?([_\s]|$)/i.test(signal.metric) ? "days" : "count";
  return (
    <MetricCard label={humanizeMetric(signal.metric)} value={formatMetricValue(signal.current_value, unit, null)}>
      {signal.current_value != null && <><Comparison pct={signal.wow_pct} period="week" /><Comparison pct={signal.dod_pct} period="day" /></>}
    </MetricCard>
  );
}

const Notice = ({ children, role }: { children: ReactNode; role?: string }) => (
  <Box role={role} sx={{ ...cardSx, color: advisor.muted, fontSize: 15 }}>{children}</Box>
);

const skeletonGrid = (status?: boolean) => (
  <Box sx={gridFor(4)}>
    {status && <Box component="span" role="status" sx={{ position: "absolute", left: -9999 }}>Loading revenue snapshot…</Box>}
    {Array.from({ length: 4 }, (_, i) => <Box key={i} sx={cardSx}><Skeleton width="60%" /><Skeleton width="40%" height={40} /><Skeleton width="70%" /></Box>)}
  </Box>
);

// ─── Section ──────────────────────────────────────────────────────────────────

interface RevenueSnapshotProps {
  brief: RevOpsBrief | null | undefined;
  ready: boolean;
  loading: boolean;
  error: boolean;
  profileError: boolean;
  onRetry: () => void;
  onRetryProfile: () => void;
  onOpenBrief: () => void;
  selection: PipelineSelection;
}

export default function RevenueSnapshot({ brief, ready, loading, error, profileError, onRetry, onRetryProfile, onOpenBrief, selection }: RevenueSnapshotProps) {
  const snapshot: Snapshot | undefined = brief?.revenue_snapshot;
  const { pipelineId } = selection;
  // Pipeline choices: CRM pipelines when known, otherwise the snapshot's own pipelines.
  const options = selection.pipelines.length
    ? selection.pipelines.map(p => ({ id: p.sourcePipelineId, label: p.label }))
    : (snapshot?.pipelines ?? []).map(p => ({ id: p.pipeline_id, label: p.pipeline_label ?? p.pipeline_id }));
  const entry = snapshot?.status === "available"
    ? snapshot.pipelines.find(p => p.pipeline_id === pipelineId)
      // Nothing selected and only one pipeline exists: show it, labelled.
      ?? (!pipelineId && !selection.isLoading && snapshot.pipelines.length === 1 ? snapshot.pipelines[0] : undefined)
    : undefined;
  const pipelineName = entry ? entry.pipeline_label ?? selection.label ?? entry.pipeline_id : selection.label ?? pipelineId;

  let content: ReactNode;
  let partial = false;
  if (profileError) {
    content = <Notice role="alert">Revenue snapshot requires your profile. <Button size="small" onClick={onRetryProfile}>Retry snapshot profile</Button></Notice>;
  } else if (!ready || loading) {
    content = skeletonGrid(true);
  } else if (error) {
    content = <Notice role="alert">Revenue snapshot unavailable. <Button size="small" onClick={onRetry}>Retry snapshot</Button></Notice>;
  } else if (!brief) {
    content = <Notice>No revenue snapshot yet. It appears with your first daily brief.</Notice>;
  } else if (!snapshot) {
    // Older backend without revenue_snapshot: signal-based partial view.
    partial = true;
    const all = uniqueSignals(brief);
    const scoped = all.some(s => s.pipeline != null) ? all.filter(s => s.pipeline === pipelineId) : all;
    content = scoped.length
      ? <Box key={pipelineId ?? ""} sx={gridFor(scoped.length)}>{scoped.map(s => <SignalCard key={`${s.pipeline ?? ""}|${s.metric}`} signal={s} />)}</Box>
      : <Notice>No metric signals supplied{all.length ? " for this pipeline" : ""}.</Notice>;
  } else if (snapshot.status === "unavailable") {
    content = <Notice>{UNAVAILABLE_MESSAGE[snapshot.unavailable_reason ?? "no_metrics"] ?? UNAVAILABLE_MESSAGE.no_metrics}</Notice>;
  } else if (!entry && !pipelineId && selection.isLoading) {
    content = skeletonGrid();
  } else if (!entry) {
    const available = snapshot.pipelines.map(p => p.pipeline_label ?? p.pipeline_id);
    content = (
      <Notice>
        {pipelineName ? <>No snapshot for <strong>{pipelineName}</strong> in this brief.</> : "Select a pipeline to see its snapshot."}
        {available.length > 0 && <Box component="span" sx={{ display: "block", fontSize: 13, color: advisor.subtle, mt: 0.5 }}>This brief has snapshots for: {available.join(", ")}.</Box>}
      </Notice>
    );
  } else if (!entry.metrics.length) {
    content = <Notice>No metrics were stored for {pipelineName}.</Notice>;
  } else {
    content = (
      // Keyed by pipeline so a selection change never reuses the previous pipeline's cards.
      <Box key={entry.pipeline_id} sx={gridFor(entry.metrics.length)}>
        {entry.metrics.map(metric => <SnapshotMetricCard key={metric.key} metric={metric} currencyCode={snapshot.currency_code} openPipeline={isOpenPipelineSnapshot(snapshot)} />)}
      </Box>
    );
  }

  const settled = !!brief && ready && !loading && !error && !profileError;
  const briefDate = snapshot?.brief_date ?? brief?.date;
  const generatedAt = snapshot?.generated_at ?? brief?.created_at;
  const stale = brief ? isStaleBrief(brief) : false;
  const showPicker = settled && options.length > 0;

  return (
    <Box component="section" aria-labelledby="snapshot-title">
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.25, flexWrap: "wrap" }}>
        <Typography id="snapshot-title" component="h2" sx={{ fontSize: 18, fontWeight: 700, color: advisor.text }}>
          Revenue snapshot
        </Typography>
        <InfoHint label="About the revenue snapshot">
          Source: the stored metrics of your latest daily RevOps brief, shown for one pipeline at a time and never combined across
          pipelines. “Generated” is when the brief was produced, not when CRM data last synced. Comparisons appear only when the brief
          stored a matching change signal; otherwise none is available, which is not the same as 0% change. Arrow colour shows direction
          only. {Object.values(isOpenPipelineSnapshot(snapshot) ? DEFINITIONS_V3 : DEFINITIONS).join(" ")}
        </InfoHint>
        {showPicker && (
          <TextField
            select
            size="small"
            label="Pipeline"
            value={options.some(o => o.id === pipelineId) ? pipelineId : ""}
            onChange={e => selection.select(e.target.value)}
            helperText="Applies to this snapshot and the stage chart"
            FormHelperTextProps={{ sx: { mx: 0.25, mt: 0.25, fontSize: 11.5, color: advisor.subtle } }}
            sx={{ ml: { xs: 0, sm: "auto" }, width: { xs: "100%", sm: 260 }, mt: { xs: 1, sm: 0 }, "& .MuiInputBase-root": { fontSize: 14, bgcolor: "#FFFFFF" } }}
          >
            {options.map(o => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
          </TextField>
        )}
      </Box>
      {settled && (
        <Typography component="div" sx={{ fontSize: 13, color: advisor.subtle, mt: -0.5, mb: 1.25, display: "flex", alignItems: "center", columnGap: 0.75, rowGap: 0.25, flexWrap: "wrap" }}>
          {partial
            ? <Chip size="small" label="Partial snapshot" sx={{ height: 22, fontSize: 12, fontWeight: 600, bgcolor: "#FFF4E0", color: "#8A5300", border: "1px solid #F5D9A6" }} />
            : pipelineName && <span>Pipeline: <Box component="strong" sx={{ color: advisor.muted }}>{pipelineName}</Box> ·</span>}
          <span>
            {partial
              ? "Only metrics that crossed the brief’s change threshold. Complete metrics need the updated backend."
              : `Brief ${briefDate ?? "date unknown"} · generated ${formatCreatedAt(generatedAt ?? undefined)}`}
          </span>
          {stale && !partial && <Box component="span" sx={{ color: "#8A5300", fontWeight: 600 }}>· may be stale</Box>}
          {partial && <Button size="small" onClick={onOpenBrief} sx={{ p: 0, minWidth: 0, fontSize: 13 }}>View brief signals</Button>}
        </Typography>
      )}
      {content}
    </Box>
  );
}
